/**
 * `creation_log` — `CreationLogValue` (`specs/M2-tier1-column-types.md` §5.11, `[09§A.7]`).
 *
 * *"system-generated, read-only column. You cannot update or clear its value"*. Always
 * populated (459/459) and stored in **no `cell_values` row**: it is projected from
 * `items.created_at` + `items.created_by`, and *"our importer must set it from the item, not
 * from the cell"* `[cvs§2 creation_log]`.
 *
 * `toText` is `"YYYY-MM-DD HH:MM:SS UTC"`, recorded verbatim in the corpus
 * (`0077-change-multiple-column-values.json`: `2026-09-09 17:44:52 UTC`). A formula references
 * the column as `{creation_log#Date}`, whose `#Date` subfield is `created_at` `[for§2]`.
 * The activity/automation legacy type name is `pulse-log`.
 */
import { z } from 'zod';
import { DEFAULT_SQL_REFS, defaultFilterSql } from '../sql.js';
import { formatUtcStamp, parseInstant, textShadow, toIsoSeconds } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const creationLogSettingsSchema = z.looseObject({
    mode: z.enum(['personOnly', 'dateOnly', 'personAndDate']).optional(),
    dateFormatMode: z.enum(['dateAgo', 'dateFormat']).optional(),
    hide_footer: z.boolean().optional(),
});
export const creationLogValueSchema = z.looseObject({
    created_at: z.string(),
    creator_id: z.string().nullable().optional(),
});
import { creationLogFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
function coerce(raw) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw !== 'object') {
        throw new ColumnValueError('creation_log', `expected an object, received ${typeof raw}`);
    }
    const record = raw;
    const instant = parseInstant(record.created_at);
    if (instant === null)
        return null;
    const creator = record.creator_id ?? record.created_by ?? null;
    return {
        created_at: toIsoSeconds(instant),
        creator_id: creator === null || creator === undefined ? null : String(creator),
    };
}
function renderStamp(iso) {
    const instant = parseInstant(iso ?? null);
    return instant === null ? '' : formatUtcStamp(instant);
}
export const creationLogColumn = defineColumnType({
    type: 'creation_log',
    title: 'Creation Log',
    description: 'When the item was created, and by whom. Read-only.',
    apiFragmentName: 'CreationLogValue',
    legacyTypeName: 'pulse-log',
    idPrefix: 'creation_log',
    valueSchema: creationLogValueSchema,
    settingsSchema: creationLogSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: creationLogSettingsSchema,
    writable: false,
    supportsSimpleValue: false,
    clearForms: [],
    filterConditions: creationLogFilterConditions,
    filterOperators: operatorsOf(creationLogFilterConditions),
    aggregations: ['earliest', 'latest', 'range', 'count', 'none'],
    emptyText: '',
    /**
     * 135: board `174316936` `Requester` stores `width: null` and monday renders it at 135
     * (P-022, re-measured by T-8a).
     */
    defaultWidth: 135,
    computed: {
        dependsOn() {
            return [{ kind: 'item_meta', field: 'created_at' }];
        },
        compute(ctx) {
            const instant = parseInstant(ctx.item?.createdAt ?? null);
            if (instant === null)
                return null;
            const creator = ctx.item?.createdBy ?? null;
            return {
                created_at: toIsoSeconds(instant),
                creator_id: creator === null ? null : String(creator),
            };
        },
        materialise: 'never',
        api: { value: 'stored', text: 'stored' },
    },
    parseValue(raw) {
        if (raw === null || raw === undefined)
            return null;
        throw new ColumnValueError('creation_log', 'the creation log column is read-only');
    },
    toText(value) {
        return value === null ? '' : renderStamp(value.created_at);
    },
    toSortKeys(value) {
        if (value === null)
            return { text: null, number: null, date: null };
        const instant = parseInstant(value.created_at);
        if (instant === null)
            return { text: null, number: null, date: null };
        return { text: textShadow(formatUtcStamp(instant)), number: instant.getTime(), date: instant };
    },
    isEqual(a, b) {
        return ((a?.created_at ?? null) === (b?.created_at ?? null) &&
            (a?.creator_id ?? null) === (b?.creator_id ?? null));
    },
    toActivityValue(value) {
        return value;
    },
    fromMondayValue(raw) {
        return coerce(raw);
    },
    toMondayValue(value) {
        return value === null ? null : { ...value };
    },
    toSimpleValue() {
        return null;
    },
    fromMondaySettings(raw) {
        return creationLogSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    /**
     * `creator: User!` alongside `creator_id: ID!` — recording `0258` answers
     * `{created_at, creator_id: "3733063", creator: {id: "3733063", name: "Roberto"}}`. The stub
     * carries the id; `/v2`'s `User` resolvers fill the rest in from the `users` row (T-5s).
     */
    toApiFragment(value) {
        if (value === null)
            return {};
        return {
            created_at: value.created_at,
            creator_id: value.creator_id ?? null,
            creator: value.creator_id === null || value.creator_id === undefined ? null : { id: String(value.creator_id) },
        };
    },
    /**
     * No webhook recording for this type: the conservative pass-through is monday's own `value`
     * object `[whk§7]`, which is what `toMondayValue` already produces (q330).
     */
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    bucketsOf(value) {
        // By day, like `date`: a timestamp bucket per second would have one item each.
        return value?.created_at ? [{ key: value.created_at.slice(0, 10), label: value.created_at.slice(0, 10) }] : [];
    },
    /**
     * monday splits this cell in two: *person is* against the creator and *date is* against the
     * creation date (`compare_attribute` `"person"` / `"date"`). The date half **is** the shadow;
     * the person half is the id inside the stored value. (The column is computed and materialises
     * `never`, so the SQL half only bites where a row exists — q431.)
     */
    compareAttributes: {
        person: {
            shadow: 'text',
            sql: (_cell, refs) => `${refs.item}.created_by::text`,
            keys: (value) => ({ text: value?.creator_id ?? null, number: null, date: null }),
        },
        date: {
            shadow: 'date',
            keys: (value) => {
                const instant = value === null ? null : parseInstant(value.created_at);
                return instant === null
                    ? { text: null, number: null, date: null }
                    : { text: null, number: instant.getTime(), date: instant };
            },
        },
    },
    /**
     * **Projected, so the SQL reads the item, not a shadow (T-9p).** `materialise: 'never'` means
     * no `cell_values` row exists for this column on any board, so `cv.date_value` is null for
     * every item and the shadow operators of `defaultFilterSql` matched nothing. The override
     * points them at `items.created_at`, which is the value the read path projects from — one
     * definition, so grid, filter, sort, summary and `/v2` cannot disagree.
     */
    filterSql(ctx) {
        const refs = ctx.refs ?? DEFAULT_SQL_REFS;
        return defaultFilterSql('date', ctx, `${refs.item}.created_at`);
    },
    sortSql(refs) {
        return [`${refs.item}.created_at`];
    },
    summarySql(ctx) {
        const refs = ctx.refs ?? DEFAULT_SQL_REFS;
        const date = `${refs.item}.created_at`;
        switch (ctx.aggregation) {
            case 'earliest':
                return { text: `min(${date})`, params: [], shape: 'scalar' };
            case 'latest':
                return { text: `max(${date})`, params: [], shape: 'scalar' };
            case 'range':
                return { text: `min(${date}), max(${date})`, params: [], shape: 'pair' };
            case 'count':
                return { text: `count(${refs.item}.id)`, params: [], shape: 'scalar' };
            default:
                return null;
        }
    },
});
//# sourceMappingURL=index.js.map